import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useCharacteristics,
  useAddCharacteristic,
  useDeleteCharacteristic,
} from "@/api/stockSubresources";

interface Props {
  sku: string;
}

export function CharacteristicsTab({ sku }: Props) {
  const { data: rows = [], isLoading } = useCharacteristics(sku);
  const addOne = useAddCharacteristic(sku);
  const delOne = useDeleteCharacteristic(sku);
  const [newType, setNewType] = useState("");
  const [newValue, setNewValue] = useState("");

  async function onAdd() {
    const type = newType.trim();
    const value = newValue.trim();
    if (!type && !value) return;
    try {
      await addOne.mutateAsync({ type: type || null, value: value || null, position: rows.length });
      setNewType("");
      setNewValue("");
      toast.success("Merkmal hinzugefuegt");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    }
  }

  async function onDelete(id: number) {
    try {
      await delOne.mutateAsync(id);
      toast.success("Merkmal geloescht");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Loeschen fehlgeschlagen");
    }
  }

  return (
    <Panel
      title="Merkmale"
      subtitle="Typ und Wert"
    >
      {isLoading ? (
        <div className="text-sm text-muted">Lade Merkmale…</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
              <th className="pb-2 w-10">#</th>
              <th className="pb-2">Typ</th>
              <th className="pb-2">Wert</th>
              <th className="pb-2 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-2">
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-6">
                  Keine Merkmale hinterlegt.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id} className="hover:bg-panel-2">
                <td className="py-2 font-mono text-muted text-xs">{i + 1}</td>
                <td className="py-2">{r.type ?? <span className="italic text-muted-2">—</span>}</td>
                <td className="py-2">{r.value ?? <span className="italic text-muted-2">—</span>}</td>
                <td className="py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(r.id)}
                    className="h-7 text-xs text-coral hover:text-coral"
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="bg-panel-2/50">
              <td className="py-2 font-mono text-muted text-xs">+</td>
              <td className="py-2 pr-2">
                <Input
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  placeholder="z.B. Material, Farbe, Gewicht …"
                  className="h-8 text-sm"
                />
              </td>
              <td className="py-2 pr-2">
                <Input
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Wert"
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onAdd();
                  }}
                />
              </td>
              <td className="py-2 text-right">
                <Button
                  size="sm"
                  onClick={onAdd}
                  disabled={(!newType.trim() && !newValue.trim()) || addOne.isPending}
                  className="h-7 text-xs"
                >
                  <Plus className="size-3" />
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </Panel>
  );
}
